// src/components/staff/mobile/MobileMyTasks.jsx
// Visible to: Line Crew (30) — their assigned fuel requests + delivery log

import React, { useState, useEffect, useCallback } from 'react';
import { Fuel, CheckCircle, Clock, Plane, ChevronRight, X } from 'lucide-react';
import { getMyFuelRequests, updateFuelRequestStatus, logFuelDelivery } from '../../../services/staffApi';

const GLOBAL_STYLE = `@keyframes spin { to { transform: rotate(360deg) } }`;

const STATUS_CONFIG = {
  pending:     { label: 'Pending',     color: '#eab308', bg: 'rgba(234,179,8,0.15)' },
  approved:    { label: 'Approved',    color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  in_progress: { label: 'In Progress', color: '#3b82f6', bg: 'rgba(59,130,246,0.15)' },
  completed:   { label: 'Completed',   color: '#64748b', bg: 'rgba(100,116,139,0.15)' },
};

const inputStyle = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 12, padding: '12px 14px', color: '#f1f5f9', fontSize: 15,
  outline: 'none', boxSizing: 'border-box',
  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>{label}</label>
    {children}
  </div>
);

// ─── Delivery Log Bottom Sheet ────────────────────────────────────────────────
const DeliverySheet = ({ open, req, onClose, onSaved }) => {
  const [form, setForm] = useState({ actual_gallons: '', fuel_type: 'JetA', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (req) setForm({ actual_gallons: req.quantity ?? '', fuel_type: req.fuel_type ?? 'JetA', notes: '' });
  }, [req]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.actual_gallons) { alert('Enter actual gallons delivered'); return; }
    setSaving(true);
    try {
      await logFuelDelivery(req.id, {
        actual_gallons: parseFloat(form.actual_gallons),
        fuel_type: form.fuel_type,
        notes: form.notes,
      });
      await updateFuelRequestStatus(req.id, 'completed', form.notes);
      onSaved();
      onClose();
    } catch (e) { alert('Error saving delivery log'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: '#1e293b', borderRadius: '24px 24px 0 0',
        maxHeight: '88dvh', display: 'flex', flexDirection: 'column',
        paddingBottom: 'env(safe-area-inset-bottom)',
        animation: 'slideUp 0.28s cubic-bezier(0.32,0.72,0,1)',
      }}>
        <style>{`@keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }`}</style>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: '#334155' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 20px 16px' }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: '#f1f5f9' }}>Fuel Delivery Log</span>
          <button onClick={onClose} style={{ background: '#334155', border: 'none', borderRadius: 20, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X size={15} color="#94a3b8" />
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '0 20px 24px', flex: 1 }}>
          {/* Aircraft info */}
          <div style={{ background: '#0f172a', borderRadius: 14, padding: '12px 14px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Plane size={16} color="#60a5fa" />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>{req?.tail_number}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{req?.manufacturer} {req?.model} · Requested: {req?.quantity} gal</div>
            </div>
          </div>

          <Field label="Actual Gallons Delivered *">
            <input
              type="number" inputMode="decimal"
              value={form.actual_gallons}
              onChange={e => setForm({ ...form, actual_gallons: e.target.value })}
              style={inputStyle} placeholder={`Requested: ${req?.quantity ?? '—'}`}
            />
          </Field>

          <Field label="Fuel Type">
            <select value={form.fuel_type} onChange={e => setForm({ ...form, fuel_type: e.target.value })} style={{ ...inputStyle, appearance: 'none' }}>
              <option value="JetA">Jet-A</option>
              <option value="100LL">100LL Avgas</option>
              <option value="JetA_plus">Jet-A+</option>
            </select>
          </Field>

          <Field label="Notes (optional)">
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Any issues, remarks…" style={{ ...inputStyle, resize: 'none', lineHeight: 1.5 }} />
          </Field>

          {/* Variance warning */}
          {form.actual_gallons && req?.quantity && Math.abs(parseFloat(form.actual_gallons) - parseFloat(req.quantity)) > 10 && (
            <div style={{ padding: '10px 14px', background: 'rgba(234,179,8,0.1)', border: '0.5px solid rgba(234,179,8,0.3)', borderRadius: 12, marginBottom: 14 }}>
              <span style={{ fontSize: 12, color: '#fbbf24' }}>
                ⚠ Variance of {Math.abs(parseFloat(form.actual_gallons) - parseFloat(req.quantity)).toFixed(1)} gal from requested amount
              </span>
            </div>
          )}

          <button onClick={handleSave} disabled={saving} style={{ width: '100%', padding: '16px', borderRadius: 16, background: saving ? '#1e40af' : '#22c55e', border: 'none', color: '#fff', fontSize: 16, fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}>
            {saving ? 'Saving…' : '✓ Complete & Log Delivery'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Task Card ────────────────────────────────────────────────────────────────
const TaskCard = ({ req, onStatusUpdate, onLogDelivery }) => {
  const sc = STATUS_CONFIG[req.status] ?? STATUS_CONFIG.pending;

  const handleStart = async () => {
    try { await updateFuelRequestStatus(req.id, 'in_progress'); onStatusUpdate(); }
    catch { alert('Error'); }
  };

  return (
    <div style={{
      background: '#1e293b', borderRadius: 18,
      border: req.status === 'in_progress' ? '1px solid rgba(59,130,246,0.4)' : '0.5px solid #334155',
      overflow: 'hidden',
    }}>
      {/* Status strip */}
      <div style={{ height: 4, background: sc.color, width: '100%' }} />

      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 13, background: 'rgba(249,115,22,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Fuel size={20} color="#fb923c" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>{req.tail_number}</span>
              <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: sc.bg, color: sc.color }}>{sc.label}</span>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#fb923c', marginBottom: 4 }}>{req.quantity} gallons</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              {req.manufacturer} {req.model}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
              <Clock size={11} color="#475569" />
              <span style={{ fontSize: 11, color: '#475569' }}>
                {new Date(req.scheduled_date).toLocaleDateString()} · {new Date(req.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            {req.description && (
              <p style={{ fontSize: 12, color: '#64748b', marginTop: 6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {req.description}
              </p>
            )}
          </div>
        </div>

        {/* CTA */}
        <div style={{ marginTop: 14 }}>
          {req.status === 'approved' && (
            <button onClick={handleStart} style={{ width: '100%', padding: '13px', borderRadius: 14, background: '#3b82f6', border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <ChevronRight size={16} /> Start Fueling
            </button>
          )}
          {req.status === 'in_progress' && (
            <button onClick={() => onLogDelivery(req)} style={{ width: '100%', padding: '13px', borderRadius: 14, background: '#22c55e', border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <CheckCircle size={16} /> Log Delivery & Complete
            </button>
          )}
          {req.status === 'completed' && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', color: '#64748b', fontSize: 13 }}>
              <CheckCircle size={15} color="#22c55e" /> Completed
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const MobileMyTasks = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deliveryReq, setDeliveryReq] = useState(null);
  const [filter, setFilter] = useState('active');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getMyFuelRequests(); setRequests(r.data ?? []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = requests.filter(r => ['approved', 'in_progress'].includes(r.status));
  const all = requests;
  const displayed = filter === 'active' ? active : all;

  const completed = requests.filter(r => r.status === 'completed').length;

  return (
    <div style={{ padding: '0 16px 100px' }}>
      <style>{GLOBAL_STYLE}</style>

      {/* Summary strip */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <div style={{ flex: 1, background: '#1e293b', borderRadius: 14, padding: '12px', border: '0.5px solid #334155', textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#3b82f6' }}>{active.length}</div>
          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>Active Tasks</div>
        </div>
        <div style={{ flex: 1, background: '#1e293b', borderRadius: 14, padding: '12px', border: '0.5px solid #334155', textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#22c55e' }}>{completed}</div>
          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>Completed Today</div>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[{ id: 'active', label: 'Active' }, { id: 'all', label: 'All Tasks' }].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} style={{
            flex: 1, padding: '10px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: filter === f.id ? 700 : 400,
            background: filter === f.id ? 'rgba(59,130,246,0.18)' : '#1e293b',
            color: filter === f.id ? '#3b82f6' : '#64748b',
            outline: filter === f.id ? '1px solid rgba(59,130,246,0.35)' : '0.5px solid #334155',
          }}>{f.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
          <div style={{ width: 28, height: 28, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : displayed.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155' }}>
          <CheckCircle size={36} color="#22c55e" style={{ marginBottom: 12 }} />
          <p style={{ color: '#22c55e', fontWeight: 600, marginBottom: 4 }}>No active tasks!</p>
          <p style={{ color: '#475569', fontSize: 13 }}>Check back soon for new assignments</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {displayed.map(req => (
            <TaskCard key={req.id} req={req} onStatusUpdate={load} onLogDelivery={setDeliveryReq} />
          ))}
        </div>
      )}

      <DeliverySheet open={!!deliveryReq} req={deliveryReq} onClose={() => setDeliveryReq(null)} onSaved={load} />
    </div>
  );
};

export default MobileMyTasks;